<script setup lang="ts">
// Core
import { ref, reactive, onMounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

// Libraries
import { useHead } from "@vueuse/head";
import type { FormInstance } from "element-plus";

// Services & Helpers
import Motion from "@/utils/motion";
import { REGEXP_MAIL } from "@/utils/rules";
import { createValidator, inviteQueryValidate } from "@/utils/validation";

// Stores
import { useUserStore } from "@/store/user";
import { useTeamStore } from "@/store/team";

// Types
import { InviteData } from "@/@types/invite";

// Components
import Button from "@/components/core/ButtonCore.vue";
import Input from "@/components/core/input/InputCore.vue";
import Checkbox from "@/components/core/CheckboxCore.vue";
import InviteModalNotification from "../team/InviteModalNotification.vue";

const route = useRoute();
const router = useRouter();

useHead({
  title: "Login",
  titleTemplate: "%s | Baseworks App",
  meta: [
    {
      name: "description",
      content: "Acesse sua conta fazendo login na Baseworks."
    },
    {
      name: "og:description",
      content: "Acesse sua conta fazendo login na Baseworks."
    },
    {
      name: "og:title",
      content: "Login"
    }
  ]
});

const teamStore = useTeamStore();
const userStore = useUserStore();

const loading = ref(false);
const inviteModal = ref(false);
const rememberPassword = ref(false);
const ruleFormRef = ref<FormInstance>();
const fromSubscription = ref(false);
const inviteData = ref<InviteData>({} as InviteData);

const ruleForm = reactive({
  email: createValidator([{ test: value => REGEXP_MAIL.test(value), message: "Por favor, insira um e-mail válido" }]),
  password: createValidator([{ test: value => !!value, message: "Por favor, insira sua senha" }])
});

const recreateRawQuery = (query: Record<string, string>) => {
  const rawQuery = Object.entries(query)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  return rawQuery;
};

const onLogin = async (formEl: FormInstance | undefined) => {
  userStore.error = "";

  ruleForm.email.validate();
  ruleForm.password.validate();

  if (!formEl || isDisabled.value) return;

  loading.value = true;

  const { email, password } = ruleForm;

  await userStore.loginByUsername({
    email: email?.value?.trim() ?? "",
    password: password?.value ?? ""
  });

  if (userStore.error) {
    const errorMessage = (userStore.error as string) || "";

    if (errorMessage.includes("auth/wrong-password")) {
      password.error = true;
      password.errorTxt = "Senha incorreta, tente novamente.";
    } else if (errorMessage.includes("auth/user-not-found")) {
      email.error = true;
      email.errorTxt = "Endereço de e-mail inválido, tente novamente.";
    }

    loading.value = false;
    return;
  }

  await teamStore.fetchTeams();

  const raw = recreateRawQuery(route.query as Record<string, string>);

  router.push("/dashboard" + "?" + raw);

  loading.value = false;
};

onMounted(() => {
  document.body.setAttribute("data-theme", "light");
  
  const email = route.query.email;
  userStore.error = "";

  if (email) {
    if (ruleForm.email) ruleForm.email.value = email as string;
    if (ruleForm.password) {
      ruleForm.password.error = true;
      ruleForm.password.errorTxt = "Por favor, insira sua senha";
    }
  }

  const subscription = route.query.subscription;

  if (subscription === "true") fromSubscription.value = true;

  const result = inviteQueryValidate(route.query as Record<string, string>);

  if (!(result as { error: string }).error) {
    inviteData.value = result as InviteData;
    inviteModal.value = true;
    if (ruleForm.email) ruleForm.email.value = inviteData.value.email || "";
  }
});

const isDisabled = computed(() => {
  const fields = Object.keys(ruleForm);
  const isFieldValid = (field: { value: string; error: boolean }) => field.value && !field.error;

  return fields.some(field => !isFieldValid(ruleForm[field as keyof typeof ruleForm]));
});

watch(
  () => ruleForm,
  (current, previous) => {
    if (current.email?.value !== previous.email?.value) {
      current.email?.validate();
      return;
    }

    current.password?.validate();
  }
);
</script>

<template>
  <div class="card-auth">
    <el-form
      ref="ruleFormRef"
      class="form w-100"
      :model="ruleForm"
      @submit.prevent="onLogin(ruleFormRef)"
      @keyup.enter="onLogin(ruleFormRef)"
    >
      <div class="card-auth__content">
        <Motion :delay="50">
          <h1 class="card-auth__title">Bem-vindo(a) a Baseworks</h1>
        </Motion>

        <Motion :delay="100">
          <div v-if="!fromSubscription" class="sign-up">
            <p>
              Primeira vez por aqui?
              <router-link to="/sign-up">Crie sua conta agora!</router-link>
            </p>
          </div>
          <div v-else class="subscription">
            <span>Sua conta foi criada com sucesso!</span>
            <p>
              Enviamos a senha para o seu e-mail, caso não encontre em sua caixa de entrada, por favor verifique o spam
              ou lixo eletrônico.
            </p>
          </div>
        </Motion>
      </div>

      <div class="card-auth__form">
        <Motion :delay="150">
          <Input
            id="loginEmail"
            clearable
            required
            full-width
            prevent-space
            label="E-mail"
            name="email"
            placeholder="exemplo@email.com"
            :is-error="ruleForm.email.error"
            :error-txt="ruleForm.email.errorTxt"
            :default-value="ruleForm.email.value"
            @input:update="ruleForm.email.value = $event"
            @input:blur="() => ruleForm.email.validate()"
          />
        </Motion>

        <Motion :delay="200">
          <Input
            id="loginPassword"
            clearable
            required
            full-width
            label="Senha"
            name="password"
            type="password"
            placeholder="Digite sua senha"
            :error-txt="ruleForm.password.errorTxt"
            :is-error="ruleForm.password.error"
            @input:update="ruleForm.password.value = $event"
            @input:blur="() => ruleForm.password.validate()"
          />
        </Motion>

        <Motion :delay="250">
          <div class="card-auth__footer">
            <p class="small remember-password" @click="rememberPassword = !rememberPassword">
              <Checkbox
                size="small"
                :default-value="rememberPassword"
                @checkbox:update="rememberPassword = $event"
                @click.stop
              />
              Lembrar
            </p>
            <router-link to="/password-reset">Esqueci a senha</router-link>
          </div>
        </Motion>

        <Motion :delay="300">
          <Button
            full-width
            prevent-responsive
            content="Entrar"
            :disabled="loading"
            :loading="loading"
            :disabled-style="isDisabled"
            loading-label="Entrando..."
            :action="() => onLogin(ruleFormRef)"
          />
        </Motion>
      </div>
    </el-form>
  </div>

  <template v-if="inviteModal">
    <InviteModalNotification :invite-data="inviteData" />
  </template>
</template>
